import type React from "react"
import { useCallback, useEffect, useRef, useState } from "react"
import { useSwipeable } from "react-swipeable"

export type SwipeDirection = "left" | "right" | null

export interface SwipeState {
  offset: number
  direction: SwipeDirection
  showActions: boolean
}

export interface SwipeActions {
  onSwipeLeft?: () => void
  onSwipeRight?: () => void
}

export interface UseSwipeGestureOptions {
  enabled?: boolean
  canSwipeLeft?: boolean
  canSwipeRight?: boolean
  onSwipeLeft?: () => void
  onSwipeRight?: () => void
}

export interface UseSwipeGestureReturn {
  swipeState: SwipeState
  swipeHandlers: ReturnType<typeof useSwipeable>
  fallbackTouchHandlers: {
    onTouchStart: (e: React.TouchEvent) => void
    onTouchMove: (e: React.TouchEvent) => void
    onTouchEnd: () => void
  }
  getSwipeStyle: () => React.CSSProperties
}

export function useSwipeGesture({
  enabled = true,
  canSwipeLeft = true,
  canSwipeRight = false,
  onSwipeLeft,
  onSwipeRight,
}: UseSwipeGestureOptions): UseSwipeGestureReturn {
  const [offset, setOffset] = useState(0)
  const [showActions, setShowActions] = useState(false)
  const [direction, setDirection] = useState<SwipeDirection>(null)

  const swipeTimeoutRef = useRef<number | null>(null)
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(
    null,
  )

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (swipeTimeoutRef.current) {
        clearTimeout(swipeTimeoutRef.current)
      }
    }
  }, [])

  const resetSwipe = useCallback(() => {
    setOffset(0)
    setShowActions(false)
    setDirection(null)
  }, [])

  // Safari fallback touch handling
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!enabled) return
      const touch = e.touches[0]
      touchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now(),
      }
    },
    [enabled],
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStartRef.current || !enabled) return

      const touch = e.touches[0]
      const deltaX = touch.clientX - touchStartRef.current.x
      const deltaY = touch.clientY - touchStartRef.current.y

      // Only process horizontal swipes (more horizontal than vertical movement)
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
        e.preventDefault() // Prevent scrolling

        if (deltaX < 0 && canSwipeLeft) {
          const newOffset = Math.max(deltaX, -120)
          setOffset(newOffset)
          setDirection("left")
        } else if (deltaX > 0 && canSwipeRight) {
          const newOffset = Math.min(deltaX, 120)
          setOffset(newOffset)
          setDirection("right")
        }
      }
    },
    [enabled, canSwipeLeft, canSwipeRight],
  )

  const handleTouchEnd = useCallback(() => {
    if (!touchStartRef.current || !enabled) return

    const swipeTime = Date.now() - touchStartRef.current.time
    const absOffset = Math.abs(offset)

    // Complete action if swipe was significant
    if (absOffset > 80 || (absOffset > 40 && swipeTime < 300)) {
      if (direction === "left" && onSwipeLeft) {
        onSwipeLeft()
        touchStartRef.current = null
        return
      } else if (direction === "right" && onSwipeRight) {
        onSwipeRight()
        touchStartRef.current = null
        return
      }
    }

    // Reset if not completing action
    setTimeout(resetSwipe, 200)
    touchStartRef.current = null
  }, [enabled, offset, direction, onSwipeLeft, onSwipeRight, resetSwipe])

  // Handle swipe gestures using react-swipeable
  const swipeHandlers = useSwipeable({
    onSwipedLeft: (eventData) => {
      if (canSwipeLeft && Math.abs(eventData.velocity) > 0.2 && onSwipeLeft) {
        onSwipeLeft()
      }
    },
    onSwipedRight: (eventData) => {
      if (canSwipeRight && Math.abs(eventData.velocity) > 0.2 && onSwipeRight) {
        onSwipeRight()
      }
    },
    onSwiping: (eventData) => {
      if (!enabled) return

      if (eventData.dir === "Left" && canSwipeLeft) {
        const newOffset = Math.max(eventData.deltaX, -120)
        setOffset(newOffset)
        setDirection("left")
        setShowActions(Math.abs(newOffset) > 50)
      } else if (eventData.dir === "Right" && canSwipeRight) {
        const newOffset = Math.min(eventData.deltaX, 120)
        setOffset(newOffset)
        setDirection("right")
        setShowActions(Math.abs(newOffset) > 50)
      } else {
        resetSwipe()
      }
    },
    onSwipeStart: () => {
      if (swipeTimeoutRef.current) {
        clearTimeout(swipeTimeoutRef.current)
      }
      resetSwipe()
    },
    onTouchEndOrOnMouseUp: () => {
      // Reset swipe state after a delay if gesture didn't complete
      swipeTimeoutRef.current = setTimeout(resetSwipe, 300)
    },
    preventScrollOnSwipe: true,
    trackMouse: false,
    trackTouch: true,
    delta: 10,
    swipeDuration: 500,
    touchEventOptions: { passive: false },
  })

  const getSwipeStyle = useCallback(
    (): React.CSSProperties => ({
      transform: `translate3d(${offset}px, 0, 0)`,
      transition: offset === 0 ? "transform 0.3s ease-out" : "none",
      WebkitTouchCallout: "none",
      WebkitUserSelect: "none",
      touchAction: "pan-y",
    }),
    [offset],
  )

  return {
    swipeState: { offset, direction, showActions },
    swipeHandlers: enabled
      ? swipeHandlers
      : ({} as ReturnType<typeof useSwipeable>),
    fallbackTouchHandlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    getSwipeStyle,
  }
}
